import { Component } from "@angular/core";
import { AppNavigator, NavigatorTarget } from "../../services/app-navigator";
import {
  ActionRow,
  ColumnFilterType,
  FilterTable,
  TableColumnInfo,
  TableConfiguration,
} from "../../components/table-view/table-view.component";
import { City } from "../../business/interfaces/City";
import { BehaviorSubject, Observable } from "rxjs";
import { CityAPI } from "../../business/apis/CityAPI";
import { TableAction } from "../../components/table-view/enum/TableAction";
import { MerchantNameId } from "../../business/interfaces/Merchant";
import { FilterMetaData } from "../../common/classes/FilterMetaData";
import { ListCitiesResponse } from "../../business/interfaces/response-pagination/ListCitiesResponse";
import { CityNameId } from "../../business/name-id/CityNameId";
import { CityFilterData } from "../../business/classes/CityFilterData";
import { format } from "date-fns";
import { ErrorHandling } from "../../common/classes/ErrorHandling";

@Component({
  selector: "app-list-cities",
  templateUrl: "./list-cities.component.html",
  styleUrls: ["./list-cities.component.scss"],
})
export class ListCitiesComponent {
  static navigatorName: string = NavigatorTarget.cities.valueOf();

  tableConfiguration: TableConfiguration<City> = {};
  loadData: BehaviorSubject<[any, any]>;
  listData: City[] = [];
  isScreenLoading = false;

  constructor(private cityAPI: CityAPI, private appNavigator: AppNavigator) {
    this.loadData = new BehaviorSubject<[any, any]>([[], null]);
  }

  ngOnInit(): void {
    let pagePagination = {
      show: true,
      list: [5, 10, 15, 20],
      selectedItemsPerPage: 15,
    };

    this.tableConfiguration = {
      pagePagination: pagePagination,
      tableColumnInfo: this.getColumTableInfo(),
      actionsRow: this.getActionRowTable.bind(this),
      getRowValue: this.getRowValue.bind(this),
      onCellTapped: this.onCellTapped.bind(this),
      loadData: this.loadData,
      reloadDataTable: this.reloadDataTable.bind(this),
    };
  }

  getActionRowTable<T>(item: T): ActionRow[] {
    let city = item as City;

    let deleteItem = {
      title: "Delete",
      type: "btn btn-danger",
      iconClass: "bi bi-trash",
      actionType: TableAction.REMOVE,
      onTapAction: this.onTapAction.bind(this),
    };

    let detailsItem = {
      title: "Details",
      type: "btn btn-primary",
      iconClass: "bi bi-info-circle",
      actionType: TableAction.DETAILS,
      onTapAction: this.onTapAction.bind(this),
    };

    let actionItems: [boolean, ActionRow][] = [
      [true, detailsItem],
      [true, deleteItem],
    ];


    // Filter out tuples where the condition is false and map the remaining tuples to their respective ActionRow objects
    return actionItems
      .filter(([condition]: [boolean, ActionRow]) => condition)
      .map(([, actionRow]: [boolean, ActionRow]) => actionRow);
  }

  getColumTableInfo(): TableColumnInfo[] {
    let idItem = {
      columId: CityNameId.ID,
      name: "Id",
      filterType: ColumnFilterType.text,
      isCellSelected: true,
    };

    let nameEn = {
      columId: CityNameId.NAME_EN,
      name: "Name - English",
      filterType: ColumnFilterType.text,
    };

    let nameAr = {
      columId: CityNameId.NAME_AR,
      name: "Name - Arabic",
      filterType: ColumnFilterType.text,
    };

    let country = {
      columId: CityNameId.COUNTRY,
      name: "Country",
      filterType: ColumnFilterType.text,
    };



    let createdAtItem = {
      columId: MerchantNameId.createdAt,
      name: "Date",
      filterType: ColumnFilterType.dateRang,
    };

    return [idItem, country, nameEn, nameAr,createdAtItem];
  }

  onCellTapped<T>(column: TableColumnInfo, item: T) {
    const id = column.columId;
    if (id == CityNameId.ID) {
      this.onTapCityDetails(item);
    }
  }

  getRowValue<T>(column: TableColumnInfo, item: T): any {
    const id = column.columId;
    let city = item as City;
    let value = city[id as keyof City];
    let styleClass = null;
    if (id == CityNameId.ID) {
      styleClass = "link-primary cursor-pointer";
    }

    if (
      column.filterType == ColumnFilterType.date ||
      column.filterType == ColumnFilterType.dateRang
    ) {
      const date = new Date(value as string);
      value = format(date, "MMM dd, yyyy, HH:mm:ss");
    }

    return {
      value: value,
      class: styleClass,
    };
  }

  navigateToCreateNewItem() {
    this.appNavigator.navigateTo({ target: NavigatorTarget.cityDetails });
  }

  reloadDataSource(filterData?: FilterTable | null) {
    let filter = new CityFilterData();
    filter.filterFieldTable = filterData?.filterFieldTable ?? [];
    filter.filterMetaData = filterData?.filterMetaData ?? new FilterMetaData();
    filter.sortField = filterData?.sortFieldTable ?? null;
    this.isScreenLoading = true;

    this.cityAPI.getList(filter).subscribe({
      next: (response: ListCitiesResponse) => {
        this.reloadTable(response);
        this.isScreenLoading = false;
      },
      error: (error: any) => {
        // Handle error
        this.isScreenLoading = false;
        console.error("Error:", error);
      },
    });
  }

  private onTapAction<T>(action: any, item: T) {
    let cityAction = action as TableAction;
    let city = item as City;
    let call: Observable<any>;

    if (cityAction == TableAction.REMOVE) {
      call = this.cityAPI.delete({ city: city });
    }  else if (cityAction == TableAction.DETAILS) {
      this.onTapCityDetails(item)
      return;
    }else {
      return;
    }

    this.isScreenLoading = true;
    call.subscribe({
      next: () => {
        let filter = new CityFilterData();
        this.reloadDataSource(null);
      },
      error: (error: any) => {
        // Handle error
        this.isScreenLoading = false;
        ErrorHandling.showError({message:"Can't Delete please check Data."})
      },
    });
  }

  private onTapCityDetails<T>(item: T) {
    let city = item as City;
    this.appNavigator.navigateTo({
      target: NavigatorTarget.cityDetails,
      id: city.id,
    });
  }

  private reloadTable(response: ListCitiesResponse) {
    this.listData = response.data;
    let metaData = response.meta;
    this.tableConfiguration.loadData?.next([this.listData, metaData]);
  }

  private reloadDataTable<T>(filterData: FilterTable) {
    this.reloadDataSource(filterData);
  }
}
